import { create } from "zustand";

import type { SpotifyTrackItem } from "../album/types/spotify";
import type { SpotifyTrack } from "../hooks/useSpotifyOperations";

// Type for writer selection (matches the existing component type)
export type WriterForSelection = {
  writercode: string;
  writername: string;
  caeipinumber?: string | null;
};

// Type for song writer with all the fields we need
export type SongWriter = {
  writercode: string;
  writername: string;
  writershare: number;
  writerdesignationcode: number;
  righttocollect: boolean;
};

// Type for recording data
export type Recording = {
  title: string;
  performingartist: string;
  isrc?: string;
  recordlabel?: string;
  uicbarcode?: string; // UPC barcode
  releasedate?: string;
  spotifyidentifier?: string;
  spotifypopularity?: number;
  spotifypopularitylastupdatedutc?: string;
  recordingidentifier?: string;
  recordingamendmentidentifier?: string;
};

// Type for basic song form data
export type SongFormData = {
  title: string;
  durationMins: number;
  durationSecs: number;
  alternativeTitles: string[];
  performingArtists: string[];
  isRemix: boolean;
  containsSamples: boolean;
  // Add Spotify data with proper types
  spotifyResult?: SpotifyTrackItem | SpotifyTrack | null;
  spotifyURL?: string;
  // Add recordings data
  recordings: Recording[];
};

// Define the store state
type SongCreationStoreState = {
  // Basic song information
  songData: SongFormData;

  // Writer management
  selectedWriters: WriterForSelection[];
  writers: SongWriter[];

  // UI state
  showSelectWritersModal: boolean;
  showCreateWriterModal: boolean;

  // Form state
  isDirty: boolean;
};

// Define the store actions
type SongCreationStoreActions = {
  // Song data actions
  setSongData: (data: Partial<SongFormData>) => void;
  updateSongField: <K extends keyof SongFormData>(field: K, value: SongFormData[K]) => void;

  // Writer management actions
  setSelectedWriters: (writers: WriterForSelection[]) => void;
  addSelectedWriter: (writer: WriterForSelection) => void;
  removeSelectedWriter: (writercode: string) => void;

  // Writer split actions
  setWriters: (writers: SongWriter[]) => void;
  addWriter: (writer: SongWriter) => void;
  removeWriter: (writercode: string) => void;
  updateWriter: (writercode: string, updates: Partial<SongWriter>) => void;
  updateWriterShare: (writercode: string, share: number) => void;
  updateWriterRole: (writercode: string, role: number) => void;
  updateWriterRightToCollect: (writercode: string, rightToCollect: boolean) => void;
  distributeSharesEqually: () => void;

  // Recording management actions
  addRecording: (recording: Recording) => void;
  removeRecording: (index: number) => void;
  updateRecording: (index: number, recording: Recording) => void;
  setRecordings: (recordings: Recording[]) => void;

  // Modal actions
  setShowSelectWritersModal: (show: boolean) => void;
  setShowCreateWriterModal: (show: boolean) => void;

  // Computed values
  getTotalShares: () => number;
  hasValidSplits: () => boolean;
  hasRightToCollect: () => boolean;

  // Form state management
  setIsDirty: (dirty: boolean) => void;

  // Clear all state
  clearState: () => void;

  // Initialize from existing data (for updates)
  initializeFromExisting: (songData: SongFormData, writers: SongWriter[], selectedWriters: WriterForSelection[]) => void;
};

// Default song data
const defaultSongData: SongFormData = {
  title: "",
  durationMins: 0,
  durationSecs: 0,
  alternativeTitles: [],
  performingArtists: [],
  isRemix: false,
  containsSamples: false,
  spotifyResult: null,
  spotifyURL: "",
  recordings: [],
};

// Create the store
const useSongCreationStore = create<SongCreationStoreState & SongCreationStoreActions>((set, get) => ({
  // Initial state
  songData: { ...defaultSongData },
  selectedWriters: [],
  writers: [],
  showSelectWritersModal: false,
  showCreateWriterModal: false,
  isDirty: false,

  // Song data actions
  setSongData: (data) => {
    set(state => ({
      songData: { ...state.songData, ...data },
      isDirty: true,
    }));
  },

  updateSongField: (field, value) => {
    set(state => ({
      songData: { ...state.songData, [field]: value },
      isDirty: true,
    }));
  },

  // Writer management actions
  setSelectedWriters: (selectedWriters) => {
    set({ selectedWriters, isDirty: true });
  },

  addSelectedWriter: (writer) => {
    const { selectedWriters } = get();
    if (!selectedWriters.some(w => w.writercode === writer.writercode)) {
      set({
        selectedWriters: [...selectedWriters, writer],
        isDirty: true,
      });
    }
  },

  removeSelectedWriter: (writercode) => {
    const { selectedWriters } = get();
    set({
      selectedWriters: selectedWriters.filter(w => w.writercode !== writercode),
      isDirty: true,
    });
  },

  // Writer split actions
  setWriters: (writers) => {
    set({ writers, isDirty: true });
  },

  addWriter: (writer) => {
    const { writers } = get();
    if (!writers.some(w => w.writercode === writer.writercode)) {
      set({
        writers: [...writers, writer],
        isDirty: true,
      });
    }
  },

  removeWriter: (writercode) => {
    const { writers, selectedWriters } = get();
    set({
      writers: writers.filter(w => w.writercode !== writercode),
      selectedWriters: selectedWriters.filter(w => w.writercode !== writercode),
      isDirty: true,
    });
  },

  updateWriter: (writercode, updates) => {
    const { writers } = get();
    set({
      writers: writers.map(w =>
        w.writercode === writercode ? { ...w, ...updates } : w,
      ),
      isDirty: true,
    });
  },

  updateWriterShare: (writercode, share) => {
    const { writers } = get();
    set({
      writers: writers.map(w =>
        w.writercode === writercode ? { ...w, writershare: share } : w,
      ),
      isDirty: true,
    });
  },

  updateWriterRole: (writercode, role) => {
    const { writers } = get();
    set({
      writers: writers.map(w =>
        w.writercode === writercode ? { ...w, writerdesignationcode: role } : w,
      ),
      isDirty: true,
    });
  },

  updateWriterRightToCollect: (writercode, rightToCollect) => {
    const { writers } = get();
    set({
      writers: writers.map(w =>
        w.writercode === writercode ? { ...w, righttocollect: rightToCollect } : w,
      ),
      isDirty: true,
    });
  },

  distributeSharesEqually: () => {
    const { writers } = get();
    if (writers.length === 0)
      return;

    const sharePerWriter = Number((100 / writers.length).toFixed(2));

    const updatedWriters = writers.map((writer, index) => {
      let writershare: number;
      if (index === writers.length - 1) {
        // For the last writer, calculate the remaining share to ensure total is exactly 100
        writershare = Number((100 - index * sharePerWriter).toFixed(2));
      }
      else {
        writershare = sharePerWriter;
      }

      return { ...writer, writershare };
    });

    set({ writers: updatedWriters, isDirty: true });
  },

  // Modal actions
  setShowSelectWritersModal: (show) => {
    set({ showSelectWritersModal: show });
  },

  setShowCreateWriterModal: (show) => {
    set({ showCreateWriterModal: show });
  },

  // Computed values
  getTotalShares: () => {
    const { writers } = get();
    return writers.reduce((sum, writer) => sum + (writer.writershare || 0), 0);
  },

  hasValidSplits: () => {
    const totalShares = get().getTotalShares();
    return Math.abs(totalShares - 100) < 0.01;
  },

  hasRightToCollect: () => {
    const { writers } = get();
    return writers.some(writer => writer.righttocollect === true);
  },

  // Recording management actions
  addRecording: (recording) => {
    const { songData } = get();
    set({
      songData: {
        ...songData,
        recordings: [...songData.recordings, recording],
      },
      isDirty: true,
    });
  },

  removeRecording: (index) => {
    const { songData } = get();
    const updatedRecordings = songData.recordings.filter((_, i) => i !== index);
    set({
      songData: {
        ...songData,
        recordings: updatedRecordings,
      },
      isDirty: true,
    });
  },

  updateRecording: (index, recording) => {
    const { songData } = get();
    const updatedRecordings = songData.recordings.map((r, i) => (i === index ? recording : r));
    set({
      songData: {
        ...songData,
        recordings: updatedRecordings,
      },
      isDirty: true,
    });
  },

  setRecordings: (recordings) => {
    const { songData } = get();
    set({
      songData: {
        ...songData,
        recordings: [...recordings],
      },
      isDirty: true,
    });
  },

  // Form state management
  setIsDirty: (dirty) => {
    set({ isDirty: dirty });
  },

  // Clear all state
  clearState: () => {
    set({
      songData: { ...defaultSongData },
      selectedWriters: [],
      writers: [],
      showSelectWritersModal: false,
      showCreateWriterModal: false,
      isDirty: false,
    });
  },

  // Initialize from existing data (for updates)
  initializeFromExisting: (songData, writers, selectedWriters) => {
    set({
      songData: { ...songData },
      writers: [...writers],
      selectedWriters: [...selectedWriters],
      isDirty: false,
    });
  },
}));

export default useSongCreationStore;
